import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, Clock } from "lucide-react";
import { VowHeader } from "@/components/VowHeader";

const Cart = () => {
  const { roomNumber, brandName } = useParams();
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const cartKey = `cart_${roomNumber}_${brandName}`;
    const saved = localStorage.getItem(cartKey);
    if (saved) { try { setCart(JSON.parse(saved)); } catch { localStorage.removeItem(cartKey); } }
    setInitialized(true);
  }, [roomNumber, brandName]);

  useEffect(() => {
    if (initialized) localStorage.setItem(`cart_${roomNumber}_${brandName}`, JSON.stringify(cart));
  }, [cart, roomNumber, brandName, initialized]);

  const updateQty = (name, delta) => {
    const updated = cart.map(i => i.name === name ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i).filter(i => i.quantity > 0);
    setCart(updated);
    localStorage.setItem(`cart_${roomNumber}_${brandName}`, JSON.stringify(updated));
  };

  const remove = (name) => {
    const updated = cart.filter(i => i.name !== name);
    setCart(updated);
    localStorage.setItem(`cart_${roomNumber}_${brandName}`, JSON.stringify(updated));
  };

  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <div className="min-h-screen bg-background pb-40">
      <VowHeader roomNumber={roomNumber} onBack={() => navigate(`/menu/${roomNumber}/${encodeURIComponent(brandName)}`)} />

      <div className="px-4 pt-5 pb-4">
        <h2 className="text-lg font-bold text-[#1c3b2e] mb-4">Your Order</h2>

        {cart.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#2c2c2c]/50 mb-4">Your cart is empty</p>
            <button onClick={() => navigate(`/menu/${roomNumber}/${encodeURIComponent(brandName)}`)}
              className="bg-[#1c3b2e] text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-[#1c3b2e]/90 min-h-[44px]"
              data-testid="browse-menu-button">
              Browse Menu
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {cart.map(item => (
              <div key={item.name} className="bg-white rounded-xl p-4 border border-[#1c3b2e]/5"
                data-testid={`cart-item-${item.name}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 min-w-0 pr-2">
                    <h3 className="font-semibold text-sm text-[#2c2c2c] leading-snug">{item.name}</h3>
                    <p className="text-xs text-[#2c2c2c]/50 mt-0.5">₹{item.price} each</p>
                  </div>
                  <button onClick={() => remove(item.name)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    data-testid={`remove-${item.name}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 bg-[#f5f1e6] rounded-lg">
                    <button onClick={() => updateQty(item.name, -1)}
                      className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-[#1c3b2e]/5 transition-colors min-h-[44px] min-w-[44px]"
                      data-testid={`decrease-${item.name}`}>
                      <Minus className="h-4 w-4 text-[#1c3b2e]" />
                    </button>
                    <span className="w-8 text-center font-bold text-sm text-[#2c2c2c]" data-testid={`quantity-${item.name}`}>
                      {item.quantity}
                    </span>
                    <button onClick={() => updateQty(item.name, 1)}
                      className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-[#1c3b2e]/5 transition-colors min-h-[44px] min-w-[44px]"
                      data-testid={`increase-${item.name}`}>
                      <Plus className="h-4 w-4 text-[#1c3b2e]" />
                    </button>
                  </div>
                  <span className="font-bold text-[#1c3b2e]">₹{(item.price * item.quantity).toFixed(0)}</span>
                </div>
              </div>
            ))}

            {/* Delivery Estimate */}
            <div className="flex items-center gap-2 bg-[#1c3b2e]/5 rounded-xl px-4 py-3 mt-4">
              <Clock className="h-4 w-4 text-[#1c3b2e]/60" />
              <span className="text-xs text-[#2c2c2c]/60">Estimated delivery</span>
              <span className="ml-auto text-xs font-semibold text-[#1c3b2e]" data-testid="delivery-time">45-60 min</span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Checkout Bar */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#1c3b2e]/10 p-4">
          <div className="max-w-md mx-auto">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-[#2c2c2c]/60">Subtotal</span>
              <span className="text-xl font-bold text-[#1c3b2e]" data-testid="total-amount">₹{total.toFixed(0)}</span>
            </div>
            <button onClick={() => navigate(`/checkout/${roomNumber}/${encodeURIComponent(brandName)}`)}
              className="w-full bg-[#1c3b2e] text-white rounded-xl h-14 font-semibold text-base hover:bg-[#1c3b2e]/90 transition-colors min-h-[56px]"
              data-testid="proceed-checkout-button">
              Proceed to Checkout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
